import { OverlayRectangle } from '@policyvision/contracts';
import { useCallback, useEffect, useRef } from 'react';

interface BoundingBoxWithUuid extends OverlayRectangle {
  sectionUuid: string;
}

type PageElementsMap = Map<number, HTMLDivElement>;

interface UsePdfScrollReturn {
  scrollToSection: (sectionUuid: string) => void;
}

/**
 * Hook to manage scrolling behavior for PDF pages based on section selection.
 * Automatically scrolls to the page containing the selected section's bounding box.
 */
export function usePdfScroll(
  pageElements: PageElementsMap,
  boundingBoxes: BoundingBoxWithUuid[],
  selectedSectionUuid: string | null,
): UsePdfScrollReturn {
  // Read through a ref so a new boundingBoxes array does not scroll back to the selection
  const boundingBoxesRef = useRef(boundingBoxes);

  useEffect(() => {
    boundingBoxesRef.current = boundingBoxes;
  }, [boundingBoxes]);

  const scrollToSection = useCallback(
    (sectionUuid: string) => {
      const selectedBox = boundingBoxesRef.current.find(
        (box) => box.sectionUuid === sectionUuid,
      );

      if (selectedBox) {
        const pageElement = pageElements.get(selectedBox.pageNumber);
        if (pageElement) {
          pageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    },
    [pageElements],
  );

  // Auto-scroll when selectedSectionUuid changes
  useEffect(() => {
    if (selectedSectionUuid) {
      scrollToSection(selectedSectionUuid);
    }
  }, [selectedSectionUuid, scrollToSection]);

  return {
    scrollToSection,
  };
}
